import type { AdditionalQueryParams } from '../../../hooks/usePagination';
import {
  type DateRange,
  readDateRange,
  replaceDateRange,
  replaceParam,
  replaceParams,
  toActiveFilter,
  toArrayFilter,
  toDateFilter,
} from './filterParamUtils';

type NumberRange = {
  from: string | null;
  to: string | null;
};

export type PlayerFilterData = {
  campaignId: string | null;
  isActive: string;
  country: string[];
  createdAt: DateRange;
  firstName: string[];
  lastName: string[];
  phone: string[];
  email: string[];
  zip: string[];
  city: string[];
  region: string[];
  language: string[];
  age: NumberRange;
};

const ARRAY_PARAMS = [
  'country',
  'firstName',
  'lastName',
  'phone',
  'email',
  'zip',
  'city',
  'region',
  'language',
] as const;

const parse = (params: URLSearchParams): PlayerFilterData => ({
  campaignId: params.get('campaignId'),
  isActive: params.get('isActive') ?? '',
  country: params.getAll('country'),
  createdAt: readDateRange(params),
  firstName: params.getAll('firstName'),
  lastName: params.getAll('lastName'),
  phone: params.getAll('phone'),
  email: params.getAll('email'),
  zip: params.getAll('zip'),
  city: params.getAll('city'),
  region: params.getAll('region'),
  language: params.getAll('language'),
  age: {
    from: params.get('ageFrom'),
    to: params.get('ageTo'),
  },
});

const write = (current: URLSearchParams, data: PlayerFilterData) => {
  const params = new URLSearchParams(current);

  replaceParam(params, 'campaignId', data.campaignId);
  replaceParam(params, 'isActive', data.isActive);
  ARRAY_PARAMS.forEach((key) => replaceParams(params, key, data[key]));
  replaceDateRange(params, data.createdAt);
  replaceParam(params, 'ageFrom', data.age.from);
  replaceParam(params, 'ageTo', data.age.to);

  return params;
};

const toApiQuery = (data: PlayerFilterData): AdditionalQueryParams => ({
  campaignId: data.campaignId || undefined,
  filters: {
    isActive: toActiveFilter(data.isActive),
    country: toArrayFilter(data.country),
    createdAt: toDateFilter(data.createdAt),
    firstName: toArrayFilter(data.firstName),
    lastName: toArrayFilter(data.lastName),
    phone: toArrayFilter(data.phone),
    email: toArrayFilter(data.email),
    zip: toArrayFilter(data.zip),
    city: toArrayFilter(data.city),
    region: toArrayFilter(data.region),
    language: toArrayFilter(data.language),
    age:
      data.age.from || data.age.to
        ? {
            from: data.age.from ? Number(data.age.from) : null,
            to: data.age.to ? Number(data.age.to) : null,
          }
        : undefined,
  },
});

export const playerFilterParams = {
  parse,
  write,
  toApiQuery,
};
